import { useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';
import {
  debug,
  getCardRemovalAnimationDuration,
  trackEvent,
} from '@/shared/lib';

import { ModalID, modalModel } from '@/entities/modal';
import { notificationModel } from '@/entities/notification';
import { transcriptorModel } from '@/entities/transcriptor';

type HideConsultationOptions = {
  /** Called when the hide request fails so the caller can roll back optimistic UI. */
  onError?: () => void;
};

/**
 * Resolves `true` as soon as the user confirms so the card can start its
 * removal animation; the request itself is sent after the animation. A failed
 * request shows an error notification and reports back through `onError`.
 */
export const useHideConsultation = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  return async (
    consultationId: string,
    options?: HideConsultationOptions,
  ): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.Confirm,
          data: {
            title: formatMessage({
              id: 'transcriptor.hideConsultation.title',
              defaultMessage: 'Archive recording',
            }),
            okText: formatMessage({
              id: 'transcriptor.hideConsultation.apply',
              defaultMessage: 'Yes, archive',
            }),
            description: formatMessage({
              id: 'transcriptor.hideConsultation.message',
              defaultMessage:
                'The recording will be hidden from the active recordings list. It stays in the system and can be restored by the clinic owner or administrator.',
            }),
            onConfirm: async () => {
              trackEvent({
                eventName:
                  'PatientProfilePageTranscriptorWidgetRemoveButtonClicked',
              });

              dispatch(modalModel.actions.closeModal(ModalID.Confirm));
              resolve(true);

              setTimeout(async () => {
                try {
                  await dispatch(
                    transcriptorModel.thunks.archiveConsultation(
                      consultationId,
                    ),
                  ).unwrap();
                } catch (error) {
                  debug('Error hiding consultation:', error);

                  dispatch(
                    notificationModel.actions.addNotification({
                      id: Date.now().toString(),
                      type: 'error',
                      message: formatMessage({
                        id: 'transcriptor.hideConsultation.error',
                        defaultMessage:
                          'Failed to hide the consultation. Please try again.',
                      }),
                    }),
                  );
                  options?.onError?.();
                }
              }, getCardRemovalAnimationDuration());
            },
            onCancel: () => {
              resolve(false);
            },
          },
        }),
      );
    });
};

export const useRestoreConsultation = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  return async (consultationId: string): Promise<boolean> => {
    try {
      await dispatch(
        transcriptorModel.thunks.restoreConsultation(consultationId),
      ).unwrap();

      return true;
    } catch (error) {
      debug('Error restoring consultation:', error);

      dispatch(
        notificationModel.actions.addNotification({
          id: Date.now().toString(),
          type: 'error',
          message: formatMessage({
            id: 'consultationOptions.restore.error',
            defaultMessage:
              'Failed to restore the consultation. Please try again.',
          }),
        }),
      );

      return false;
    }
  };
};
